/**
 * Zustand zum Meldezeitpunkt (docs/testing-tickets-plan.md, Abschnitt 2).
 *
 * Der Wert eines Tickets steckt weniger im Text als in dem, was drumherum galt:
 * Welche Seite, welcher Standort, welche Version, welcher Aufruf ist gescheitert.
 * „Speichern geht nicht" wird damit von einer Frage zu einer Spur.
 *
 * Bewusst **keine** Formularinhalte und keine Antwortkörper. Was mitgeht, kann
 * der Melder vor dem Absenden aufklappen.
 */

import {
  konsolenPuffer,
  requestPuffer,
  type KonsolenEintrag,
  type RequestEintrag,
} from './contextBuffer';
import { aktuellerWorkflow, type WorkflowStand } from './workflow';

declare const __APP_VERSION__: string;

export interface MeldeKontext {
  route: string;
  routePattern?: string;
  standortId?: number | null;
  appVersion: string;
  userAgent: string;
  viewport: { w: number; h: number };
  locale: string;
  timezone: string;
  console: KonsolenEintrag[];
  requests: RequestEintrag[];
  /** Der Assistent, in dem gemeldet wurde — samt Schritt. */
  workflow?: WorkflowStand;
  boundary?: { message: string; stack?: string; componentStack?: string };
}

const sicher = <T>(wert: () => T, ersatz: T): T => {
  try {
    return wert();
  } catch {
    return ersatz;
  }
};

export function sammleKontext(options: {
  routePattern?: string;
  standortId?: number | null;
  boundary?: MeldeKontext['boundary'];
}): MeldeKontext {
  const pfad = sicher(() => window.location.pathname + window.location.search, '');
  return {
    route: pfad.slice(0, 200),
    routePattern: options.routePattern,
    standortId: options.standortId ?? null,
    appVersion: sicher(() => (typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0'), '0.0.0'),
    userAgent: sicher(() => navigator.userAgent, ''),
    viewport: sicher(() => ({ w: window.innerWidth, h: window.innerHeight }), { w: 0, h: 0 }),
    locale: sicher(() => navigator.language, 'de-DE'),
    timezone: sicher(() => Intl.DateTimeFormat().resolvedOptions().timeZone, ''),
    console: sicher(konsolenPuffer, []),
    requests: sicher(requestPuffer, []),
    ...(() => {
      const ablauf = sicher(aktuellerWorkflow, null);
      return ablauf ? { workflow: ablauf } : {};
    })(),
    ...(options.boundary ? { boundary: options.boundary } : {}),
  };
}

/** Kontext für die Vorschau „das wird mitgeschickt" — je Zeile ein Begriff. */
export function kontextZeilen(kontext: MeldeKontext, standortName?: string | null): Array<[string, string]> {
  const zeilen: Array<[string, string]> = [
    ['Seite', kontext.route || '—'],
    ['Version', kontext.appVersion],
    // Der Name statt der Nummer (T-0224); mitgeschickt wird weiter die Nummer.
    ['Standort', kontext.standortId ? (standortName || `Nr. ${kontext.standortId}`) : 'keiner gewählt'],
    ['Fenster', `${kontext.viewport.w} × ${kontext.viewport.h}`],
    ['Sprache', kontext.locale],
    ['Zeitzone', kontext.timezone || '—'],
    ['Browser', kontext.userAgent || '—'],
  ];
  if (kontext.workflow) {
    const { name, schritt, schritte, schrittName } = kontext.workflow;
    const wo = schritt
      ? ` — Schritt ${schritt}${schritte ? ` von ${schritte}` : ''}${schrittName ? `: ${schrittName}` : ''}`
      : '';
    zeilen.splice(1, 0, ['Ablauf', `${name}${wo}`]);
  }
  if (kontext.boundary) zeilen.push(['Fehlermeldung', kontext.boundary.message]);
  kontext.requests.forEach((eintrag) => {
    zeilen.push([
      'Aufruf',
      `${eintrag.method} ${eintrag.path} → ${eintrag.status}${
        eintrag.requestId ? ` (${eintrag.requestId})` : ''
      }`,
    ]);
  });
  kontext.console.forEach((eintrag) => zeilen.push(['Konsole', eintrag.message]));
  return zeilen;
}

/** Ereignis, mit dem eine beliebige Stelle den Melde-Dialog öffnet. */
export const MELDE_EREIGNIS = 'sidigo:fehler-melden';

export interface MeldeAnstoss {
  boundary?: MeldeKontext['boundary'];
  titel?: string;
}

/**
 * Öffnet den Melde-Dialog von außerhalb des React-Baums.
 *
 * Die ErrorBoundary ist eine Klassenkomponente oberhalb der Provider — ein
 * Ereignis ist dort der kürzere Weg als Kontext durch die halbe Anwendung.
 */
export function oeffneMeldung(anstoss: MeldeAnstoss = {}): void {
  try {
    window.dispatchEvent(new CustomEvent<MeldeAnstoss>(MELDE_EREIGNIS, { detail: anstoss }));
  } catch {
    // Ohne Ereignis bleibt der schwebende Knopf; das ist kein Grund zu scheitern.
  }
}
